import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useJeopardyContext } from './jeopardy-context';
import {
    JeopardyBoardConfig,
    JeopardyClue,
    JeopardyGameState,
    JeopardyPhase,
} from './jeopardy-types';

interface Props {
    boardConfig: JeopardyBoardConfig;
    timerSeconds: number;
}

interface ConfirmOptions {
    title: string;
    message: string;
    onConfirm: () => void;
}

function ConfirmDialog({ title, message, onConfirm, onCancel }: ConfirmOptions & { onCancel: () => void }) {
    return (
        <div className="j-confirm-backdrop" onClick={onCancel}>
            <div className="j-confirm-dialog" onClick={e => e.stopPropagation()}>
                <h4>{title}</h4>
                <p>{message}</p>
                <div className="j-confirm-dialog__actions">
                    <button className="j-confirm-dialog__cancel" onClick={onCancel}>Cancel</button>
                    <button className="j-confirm-dialog__confirm" onClick={onConfirm}>Confirm</button>
                </div>
            </div>
        </div>
    );
}

function synthesizeBeep(freq: number, duration: number) {
    try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.1, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
        osc.start();
        osc.stop(ctx.currentTime + duration);
    } catch (_) {}
}

function findClueById(board: JeopardyBoardConfig, id: string, type: 'single' | 'double' | null): JeopardyClue | null {
    const cats = type === 'double' ? board.doubleJeopardyCategories : board.jeopardyCategories;
    for (const cat of cats) {
        const found = cat.clues.find(c => c.id === id);
        if (found) return found;
    }
    return null;
}

function checkRoundComplete(board: JeopardyBoardConfig, phase: JeopardyPhase, playedClues: Record<string, boolean>): boolean {
    const cats = phase === 'double_jeopardy' ? board.doubleJeopardyCategories : board.jeopardyCategories;
    for (const cat of cats) {
        for (const clue of cat.clues) {
            if (!playedClues[clue.id]) return false;
        }
    }
    return true;
}

function getCategoryForClue(board: JeopardyBoardConfig, clueId: string, type: 'single' | 'double' | null): string {
    const cats = type === 'double' ? board.doubleJeopardyCategories : board.jeopardyCategories;
    for (const cat of cats) {
        if (cat.clues.some(c => c.id === clueId)) return cat.name;
    }
    return '';
}

export const JeopardyGameOverview = ({ boardConfig, timerSeconds: initialTimerSeconds }: Props) => {
    const { gameState, updateGameState, loading } = useJeopardyContext();

    const [localTimer, setLocalTimer] = useState(initialTimerSeconds);
    const [isTimerRunning, setIsTimerRunning] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [currentWagerDrafts, setCurrentWagerDrafts] = useState<Record<string, string>>({});
    const [ddWagerDraft, setDdWagerDraft] = useState('');
    const [customScoreAmt, setCustomScoreAmt] = useState<Record<string, string>>({});
    const [confirmOpts, setConfirmOpts] = useState<ConfirmOptions | null>(null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const activeClue: JeopardyClue | null = gameState.activeClueId
        ? findClueById(boardConfig, gameState.activeClueId, gameState.activeClueType)
        : null;

    const hasCorrectAnswer = Object.values(gameState.answeredByTeam).includes('correct');
    const finalTimerSeconds = 30;

    const isDailyDouble = (clueId: string) =>
        !!(boardConfig.dailyDoubleClueIds && boardConfig.dailyDoubleClueIds.includes(clueId));

    // Local timer management
    useEffect(() => {
        if (isTimerRunning && localTimer > 0) {
            timerRef.current = setTimeout(() => {
                const next = localTimer - 1;
                setLocalTimer(next);
                if (next <= 3 && next > 0 && soundEnabled) synthesizeBeep(880, 0.1);
                else if (next === 0) {
                    setIsTimerRunning(false);
                    if (soundEnabled) synthesizeBeep(440, 0.6);
                }
            }, 1000);
        }
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, [isTimerRunning, localTimer, soundEnabled, updateGameState]);

    const pushState = useCallback(
        (patch: Partial<JeopardyGameState>) => updateGameState(patch),
        [updateGameState]
    );

    const confirmAction = (opts: ConfirmOptions) => setConfirmOpts(opts);

    const handleOpenClue = (clue: JeopardyClue, type: 'single' | 'double') => {
        if (gameState.playedClues[clue.id]) return;

        // If Daily Double — set pending state, no timer yet
        if (isDailyDouble(clue.id)) {
            setDdWagerDraft('');
            pushState({
                pendingDailyDoubleClueId: clue.id,
                pendingDailyDoubleTeamId: null,
                pendingDailyDoubleWager: null,
                activeClueId: null,
                activeClueType: type,
            });
            return;
        }

        if (soundEnabled) synthesizeBeep(587.33, 0.25);
        setLocalTimer(initialTimerSeconds);
        setIsTimerRunning(true);
        pushState({
            activeClueId: clue.id,
            activeClueType: type,
            clueRevealed: false,
            answeredByTeam: {},
            remainingSeconds: initialTimerSeconds,
            pendingDailyDoubleClueId: null,
        });
    };

    const handleDdWagerSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!gameState.pendingDailyDoubleClueId) return;
        const val = parseInt(ddWagerDraft);
        if (isNaN(val) || val <= 0) return;

        if (soundEnabled) synthesizeBeep(587.33, 0.25);
        setLocalTimer(initialTimerSeconds);
        setIsTimerRunning(true);
        pushState({
            activeClueId: gameState.pendingDailyDoubleClueId,
            activeClueType: gameState.activeClueType,
            pendingDailyDoubleClueId: null,
            pendingDailyDoubleWager: val,
            clueRevealed: false,
            answeredByTeam: {},
            remainingSeconds: initialTimerSeconds,
        });
    };

    const handleCloseClue = () => {
        if (!activeClue) return;
        const nextPlayed = { ...gameState.playedClues, [activeClue.id]: true };
        setIsTimerRunning(false);
        pushState({
            activeClueId: null,
            activeClueType: null,
            clueRevealed: false,
            answeredByTeam: {},
            playedClues: nextPlayed,
            pendingDailyDoubleWager: null,
        });
    };

    const handleRevealClue = () => {
        setIsTimerRunning(false);
        pushState({ clueRevealed: true });
    };

    const handleScoreAdjustment = (teamId: string, isCorrect: boolean) => {
        if (!activeClue || hasCorrectAnswer) return;
        const pts = gameState.pendingDailyDoubleWager ?? activeClue.points;
        const nextTeams = gameState.teams.map(t =>
            t.id === teamId ? { ...t, score: isCorrect ? t.score + pts : t.score - pts } : t
        );
        const nextAnswered = { ...gameState.answeredByTeam, [teamId]: isCorrect ? 'correct' as const : 'incorrect' as const };

        if (isCorrect) {
            const nextPlayed = { ...gameState.playedClues, [activeClue.id]: true };
            setIsTimerRunning(false);
            if (soundEnabled) synthesizeBeep(1046.5, 0.4);
            pushState({
                teams: nextTeams,
                answeredByTeam: nextAnswered,
                clueRevealed: true,
                playedClues: nextPlayed,
            });
        } else {
            if (soundEnabled) synthesizeBeep(261.63, 0.35);
            pushState({ teams: nextTeams, answeredByTeam: nextAnswered });
        }
    };

    const handleUndoScoreAdjustment = (teamId: string) => {
        if (!activeClue) return;
        const status = gameState.answeredByTeam[teamId];
        if (!status) return;
        const pts = gameState.pendingDailyDoubleWager ?? activeClue.points;
        const nextTeams = gameState.teams.map(t =>
            t.id === teamId ? { ...t, score: status === 'correct' ? t.score - pts : t.score + pts } : t
        );
        const nextAnswered = { ...gameState.answeredByTeam };
        delete nextAnswered[teamId];
        let patch: Partial<JeopardyGameState> = { teams: nextTeams, answeredByTeam: nextAnswered };
        if (status === 'correct') {
            const nextPlayed = { ...gameState.playedClues };
            delete nextPlayed[activeClue.id];
            patch = { ...patch, playedClues: nextPlayed, clueRevealed: false };
            setIsTimerRunning(true);
        }
        pushState(patch);
    };

    const handleManualScoreEdit = (teamId: string, delta: number) => {
        const nextTeams = gameState.teams.map(t =>
            t.id === teamId ? { ...t, score: t.score + delta } : t
        );
        pushState({ teams: nextTeams });
    };

    const handleCustomScoreApply = (teamId: string, sign: 1 | -1) => {
        const raw = customScoreAmt[teamId];
        const val = parseInt(raw);
        if (isNaN(val) || val <= 0) return;
        handleManualScoreEdit(teamId, sign * val);
        setCustomScoreAmt(prev => ({ ...prev, [teamId]: '' }));
    };

    const handleAdvancePhase = () => {
        const { phase } = gameState;
        if (phase === 'single_jeopardy') {
            const nextPhase: JeopardyPhase = boardConfig.hasDoubleJeopardy ? 'double_jeopardy' : 'final_jeopardy_wager';
            pushState({ phase: nextPhase, activeClueId: null, activeClueType: null });
        } else if (phase === 'double_jeopardy') {
            pushState({ phase: 'final_jeopardy_wager', activeClueId: null, activeClueType: null });
        }
    };

    const handleForceAdvance = () => {
        const { phase } = gameState;
        if (phase !== 'single_jeopardy' && phase !== 'double_jeopardy') return;
        const label = phase === 'single_jeopardy' ? 'Single Jeopardy' : 'Double Jeopardy';
        const nextPhase: JeopardyPhase = phase === 'single_jeopardy'
            ? (boardConfig.hasDoubleJeopardy ? 'double_jeopardy' : 'final_jeopardy_wager')
            : 'final_jeopardy_wager';
        confirmAction({
            title: 'Force Advance Round',
            message: `Skip remaining clues in ${label} and advance to the next round?`,
            onConfirm: () => {
                setIsTimerRunning(false);
                pushState({ phase: nextPhase, activeClueId: null, activeClueType: null });
            },
        });
    };

    // Final Jeopardy wager — any team can submit independently
    const handleSubmitWager = (teamId: string, draftValue: string) => {
        const val = parseInt(draftValue);
        if (isNaN(val) || val < 0) return;
        const team = gameState.teams.find(t => t.id === teamId);
        if (!team) return;
        const maxAllowed = team.score > 0 ? team.score : (boardConfig.rowsCount * 400);
        if (val > maxAllowed) return;
        const nextWagers = { ...gameState.wagers, [teamId]: val };
        // Clear the draft for this team
        setCurrentWagerDrafts(prev => {
            const next = { ...prev };
            delete next[teamId];
            return next;
        });
        // Check if all teams have wagered
        const allWagered = gameState.teams.every(t => nextWagers[t.id] !== undefined);
        if (allWagered) {
            setLocalTimer(finalTimerSeconds);
            setIsTimerRunning(true);
            pushState({ wagers: nextWagers, phase: 'final_jeopardy_question', remainingSeconds: finalTimerSeconds });
        } else {
            pushState({ wagers: nextWagers });
        }
    };

    const handleRevealFinalQuestion = () => {
        setIsTimerRunning(false);
        pushState({ phase: 'final_jeopardy_evaluation', clueRevealed: true });
    };

    const handleFinalEvaluation = (teamId: string, isCorrect: boolean) => {
        const wagerAmt = gameState.wagers[teamId] || 0;
        const nextTeams = gameState.teams.map(t =>
            t.id === teamId ? { ...t, score: isCorrect ? t.score + wagerAmt : t.score - wagerAmt } : t
        );
        const nextEvals = { ...gameState.finalEvaluations, [teamId]: isCorrect };
        // Check if all teams have been evaluated
        const allEvaluated = gameState.teams.every(t => nextEvals[t.id] !== undefined && nextEvals[t.id] !== null);
        if (allEvaluated) {
            if (soundEnabled) synthesizeBeep(1174.66, 0.8);
            // Check for tie — offer tie-breaker
            const finalTeams = nextTeams;
            const maxScore = Math.max(...finalTeams.map(t => t.score));
            const winners = finalTeams.filter(t => t.score === maxScore);
            if (winners.length > 1) {
                pushState({ teams: finalTeams, finalEvaluations: nextEvals, phase: 'tie_breaker' });
            } else {
                pushState({ teams: finalTeams, finalEvaluations: nextEvals, phase: 'game_over' });
            }
        } else {
            pushState({ teams: nextTeams, finalEvaluations: nextEvals });
        }
    };

    const handleResetGame = () => {
        confirmAction({
            title: 'Reset Game',
            message: 'All scores and progress will be lost. Are you sure?',
            onConfirm: () => {
                setIsTimerRunning(false);
                setLocalTimer(initialTimerSeconds);
                pushState({
                    phase: 'single_jeopardy',
                    teams: gameState.teams.map(t => ({ ...t, score: 0 })),
                    playedClues: {},
                    activeClueId: null,
                    activeClueType: null,
                    clueRevealed: false,
                    answeredByTeam: {},
                    wagers: {},
                    finalAnswers: {},
                    finalEvaluations: {},
                    wagerStepTeamIdx: 0,
                    evalStepTeamIdx: 0,
                    remainingSeconds: initialTimerSeconds,
                    pendingDailyDoubleClueId: null,
                    pendingDailyDoubleTeamId: null,
                    pendingDailyDoubleWager: null,
                });
            },
        });
    };

    const handleOpenPlayerScreen = () => {
        const url = window.location.href.replace('/overview', '').replace(/\?.*$/, '');
        window.open(url, '_blank', 'noopener');
    };

    const { phase, teams, playedClues, answeredByTeam, wagers } = gameState;
    const currentCats = phase === 'double_jeopardy' ? boardConfig.doubleJeopardyCategories : boardConfig.jeopardyCategories;
    const roundComplete = (phase === 'single_jeopardy' || phase === 'double_jeopardy') && checkRoundComplete(boardConfig, phase, playedClues);
    const sortedTeams = [...teams].sort((a, b) => b.score - a.score);

    if (loading) return <div className="jeopardy-overview__loading">Loading game state…</div>;

    const showBoard = (phase === 'single_jeopardy' || phase === 'double_jeopardy') && !activeClue && !gameState.pendingDailyDoubleClueId;
    const showClueModal = !!(activeClue && (phase === 'single_jeopardy' || phase === 'double_jeopardy'));
    const showDdWager = !!(gameState.pendingDailyDoubleClueId && !activeClue);

    return (
        <div className="jeopardy-overview">
            {/* Header */}
            <div className="jeopardy-overview__header">
                <div className="jeopardy-overview__title-row">
                    <h2 className="jeopardy-overview__title">🏆 Master Console</h2>
                    <div className="jeopardy-overview__phase-badges">
                        <span className={`jeopardy-overview__phase-badge${phase === 'single_jeopardy' ? ' --active' : ''}`}>Single</span>
                        {boardConfig.hasDoubleJeopardy && (
                            <span className={`jeopardy-overview__phase-badge${phase === 'double_jeopardy' ? ' --active' : ''}`}>Double</span>
                        )}
                        <span className={`jeopardy-overview__phase-badge${phase.startsWith('final_jeopardy') ? ' --active' : ''}`}>Final</span>
                        {phase === 'tie_breaker' && (
                            <span className="jeopardy-overview__phase-badge --active">Tie</span>
                        )}
                        <span className={`jeopardy-overview__phase-badge${phase === 'game_over' ? ' --active' : ''}`}>Done</span>
                    </div>
                </div>
                <div className="jeopardy-overview__controls">
                    <button onClick={handleOpenPlayerScreen} title="Open player screen in new tab">
                        <span className="jeopardy-overview__player-link-dot" />
                        Player Screen
                    </button>
                    <button
                        className="jeopardy-overview__sound-btn"
                        onClick={() => setSoundEnabled(s => !s)}
                        title={soundEnabled ? 'Mute sounds' : 'Unmute sounds'}
                    >
                        {soundEnabled ? '🔊' : '🔇'}
                    </button>
                    <button className="jeopardy-overview__reset-btn" onClick={handleResetGame}>
                        ↺ Reset
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="jeopardy-overview__body">
                {/* Sidebar leaderboard */}
                <div className="jeopardy-overview__sidebar">
                    <div className="jeopardy-overview__sidebar-title">Leaderboard</div>
                    {sortedTeams.map((team, idx) => (
                        <div key={team.id} className="jeopardy-overview__sidebar-team">
                            <span className="jeopardy-overview__sidebar-rank">{idx + 1}.</span>
                            <span className="jeopardy-overview__sidebar-avatar">{team.avatar}</span>
                            <div className="jeopardy-overview__sidebar-info">
                                <div className="jeopardy-overview__sidebar-name">{team.name}</div>
                                <div className={`jeopardy-overview__sidebar-score${team.score < 0 ? ' --negative' : ''}`}>
                                    ${team.score.toLocaleString()}
                                </div>
                                {/* Custom score adjust */}
                                <div className="score-adj-row" style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                    <input
                                        type="number"
                                        min={1}
                                        placeholder="amt"
                                        value={customScoreAmt[team.id] ?? ''}
                                        onChange={e => setCustomScoreAmt(prev => ({ ...prev, [team.id]: e.target.value }))}
                                        style={{ width: 54, padding: '2px 4px', fontSize: '0.72rem', background: 'var(--j-surface)', border: '1px solid var(--j-border)', borderRadius: 4, color: 'var(--j-text)' }}
                                    />
                                    <button
                                        style={{ background: 'rgba(16,185,129,0.2)', border: '1px solid var(--j-green)', color: 'var(--j-green)', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}
                                        onClick={() => handleCustomScoreApply(team.id, 1)}
                                        title={`+pts for ${team.name}`}
                                    >+</button>
                                    <button
                                        style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid var(--j-red)', color: 'var(--j-red)', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}
                                        onClick={() => handleCustomScoreApply(team.id, -1)}
                                        title={`-pts from ${team.name}`}
                                    >−</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Main content */}
                <div className="jeopardy-overview__main">
                    {/* Daily Double wager UI */}
                    {showDdWager && (() => {
                        const ddClue = findClueById(boardConfig, gameState.pendingDailyDoubleClueId!, gameState.activeClueType);
                        return (
                            <div className="jeopardy-overview__dd-wager">
                                <h3>⭐ Daily Double!</h3>
                                <p style={{ color: 'var(--j-muted)', margin: 0, fontSize: '0.9rem' }}>
                                    Category: <strong style={{ color: 'var(--j-text)' }}>
                                        {getCategoryForClue(boardConfig, gameState.pendingDailyDoubleClueId!, gameState.activeClueType)}
                                    </strong>
                                    {' '}· Clue worth <strong style={{ color: 'var(--j-gold)' }}>${ddClue?.points}</strong>
                                </p>
                                <form onSubmit={handleDdWagerSubmit}>
                                    <input
                                        type="number"
                                        min={1}
                                        value={ddWagerDraft}
                                        onChange={e => setDdWagerDraft(e.target.value)}
                                        placeholder="Wager amount"
                                        autoFocus
                                    />
                                    <button type="submit">Reveal Clue →</button>
                                </form>
                            </div>
                        );
                    })()}

                    {/* Active clue modal */}
                    {showClueModal && activeClue && (
                        <div className="jeopardy-overview__clue-modal">
                            <div className="jeopardy-overview__clue-header">
                                <div>
                                    <div className={`jeopardy-overview__clue-points${gameState.pendingDailyDoubleWager ? ' --dd' : ''}`}>
                                        {gameState.pendingDailyDoubleWager
                                            ? `⭐ DD · Wager $${gameState.pendingDailyDoubleWager}`
                                            : `$${activeClue.points}`}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--j-muted)' }}>
                                        {getCategoryForClue(boardConfig, activeClue.id, gameState.activeClueType)}
                                        {' '}· <span style={{ textTransform: 'capitalize' }}>{activeClue.difficulty}</span>
                                    </div>
                                </div>
                                <div className="jeopardy-overview__timer">
                                    <span className={`jeopardy-overview__timer-count${localTimer <= 5 ? ' --low' : ''}`}>
                                        {localTimer}s
                                    </span>
                                </div>
                            </div>

                            <div className="jeopardy-overview__clue-question">{activeClue.question}</div>

                            <div className="jeopardy-overview__clue-answer">
                                <strong>Answer: </strong>{activeClue.answer}
                                {!gameState.clueRevealed && (
                                    <span style={{ color: 'var(--j-muted)', marginLeft: 8, fontSize: '0.85rem' }}>
                                        (hidden from players)
                                    </span>
                                )}
                            </div>

                            <div className="jeopardy-overview__clue-actions">
                                {!gameState.clueRevealed && (
                                    <button className="jeopardy-overview__reveal-btn" onClick={handleRevealClue}>
                                        👁 Reveal to Players
                                    </button>
                                )}
                                <button className="jeopardy-overview__skip-btn" onClick={handleCloseClue}>
                                    Skip / Unanswered
                                </button>
                                {gameState.clueRevealed && (
                                    <button className="jeopardy-overview__close-clue-btn" onClick={handleCloseClue}>
                                        ✓ Done
                                    </button>
                                )}
                            </div>

                            {/* Team scoring */}
                            {!hasCorrectAnswer && (
                                <div className="jeopardy-overview__team-scoring">
                                    <p>Score for team:</p>
                                    {teams.map(team => {
                                        const status = answeredByTeam[team.id];
                                        const pts = gameState.pendingDailyDoubleWager ?? activeClue.points;
                                        return (
                                            <div key={team.id} className="jeopardy-overview__team-score-row">
                                                <span style={{ color: team.color }}>{team.avatar} {team.name}</span>
                                                {status ? (
                                                    <>
                                                        <span className={`jeopardy-overview__answer-status jeopardy-overview__answer-status--${status}`}>
                                                            {status === 'correct' ? '✓ Correct' : '✗ Wrong'}
                                                        </span>
                                                        <button
                                                            className="jeopardy-overview__undo-btn"
                                                            onClick={() => handleUndoScoreAdjustment(team.id)}
                                                        >
                                                            Undo
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            className="jeopardy-overview__correct-btn"
                                                            onClick={() => handleScoreAdjustment(team.id, true)}
                                                        >
                                                            ✓ +${pts}
                                                        </button>
                                                        <button
                                                            className="jeopardy-overview__incorrect-btn"
                                                            onClick={() => handleScoreAdjustment(team.id, false)}
                                                        >
                                                            ✗ −${pts}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Answered state: show close button */}
                            {hasCorrectAnswer && (
                                <button className="jeopardy-overview__close-clue-btn" onClick={handleCloseClue}>
                                    ✓ Close Clue
                                </button>
                            )}
                        </div>
                    )}

                    {/* Board grid */}
                    {showBoard && (
                        <div className="jeopardy-overview__board">
                            <div
                                className="jeopardy-overview__board-grid"
                                style={{ gridTemplateColumns: `repeat(${currentCats.length}, 1fr)` }}
                            >
                                {currentCats.map(cat => (
                                    <div key={cat.id} className="jeopardy-overview__board-category">
                                        {cat.name}
                                    </div>
                                ))}
                                {Array.from({ length: boardConfig.rowsCount }, (_, rowIdx) =>
                                    currentCats.map(cat => {
                                        const clue = cat.clues[rowIdx];
                                        if (!clue) return null;
                                        const played = !!playedClues[clue.id];
                                        const isActive = gameState.activeClueId === clue.id;
                                        const isDD = isDailyDouble(clue.id);
                                        return (
                                            <button
                                                key={clue.id}
                                                className={[
                                                    'jeopardy-overview__board-cell',
                                                    played ? '--played' : '',
                                                    isActive ? '--active' : '',
                                                    isDD && !played ? '--daily-double' : '',
                                                ].filter(Boolean).join(' ')}
                                                onClick={() => !played && handleOpenClue(clue, phase === 'double_jeopardy' ? 'double' : 'single')}
                                                disabled={played}
                                            >
                                                {!played && (
                                                    <>
                                                        <span className="jeopardy-overview__cell-value">${clue.points}</span>
                                                        <span className="jeopardy-overview__cell-hint">{clue.question}</span>
                                                        <span className={`jeopardy-overview__cell-difficulty --${clue.difficulty.toLowerCase()}`}>
                                                            {clue.difficulty}
                                                        </span>
                                                    </>
                                                )}
                                            </button>
                                        );
                                    })
                                )}
                            </div>

                            {/* Round controls */}
                            <div className="jeopardy-overview__round-complete" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, margin: '12px 0 0' }}>
                                {roundComplete ? (
                                    <>
                                        <span style={{ flex: 1, color: 'var(--j-gold)', fontWeight: 700, alignSelf: 'center' }}>
                                            ✓ Round complete
                                        </span>
                                        <button className="jeopardy-overview__advance-btn" onClick={handleAdvancePhase}>
                                            {phase === 'single_jeopardy' && boardConfig.hasDoubleJeopardy
                                                ? '→ Double Jeopardy'
                                                : '→ Final Jeopardy'}
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        className="jeopardy-overview__skip-btn"
                                        onClick={handleForceAdvance}
                                        style={{ fontSize: '0.8rem', padding: '6px 14px' }}
                                    >
                                        Force Advance Round ↗
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Final Jeopardy - Wager phase */}
                    {phase === 'final_jeopardy_wager' && (
                        <div className="jeopardy-overview__final">
                            <h3>Final Jeopardy — Wagers</h3>
                            <p className="jeopardy-overview__final-category">
                                Category: <strong>{boardConfig.finalJeopardy.category}</strong>
                            </p>
                            <div className="jeopardy-overview__wager-teams">
                                {teams.map(team => {
                                    const maxAllowed = team.score > 0 ? team.score : (boardConfig.rowsCount * 400);
                                    const draftValue = currentWagerDrafts[team.id] ?? '';
                                    return (
                                        <div
                                            key={team.id}
                                            className="jeopardy-overview__wager-team"
                                            style={{ borderLeftColor: team.color }}
                                        >
                                            <span style={{ flex: 1 }}>{team.avatar} {team.name}</span>
                                            <span style={{ color: 'var(--j-muted)', fontSize: '0.85rem' }}>${team.score.toLocaleString()}</span>
                                            {wagers[team.id] !== undefined ? (
                                                <span className="jeopardy-overview__wager-set">✓ ${wagers[team.id]}</span>
                                            ) : (
                                                <div className="jeopardy-overview__wager-form" style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        max={maxAllowed}
                                                        value={draftValue}
                                                        onChange={e => setCurrentWagerDrafts(prev => ({ ...prev, [team.id]: e.target.value }))}
                                                        placeholder={`0–$${maxAllowed}`}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSubmitWager(team.id, draftValue)}
                                                    >
                                                        Submit
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Final Jeopardy - Question phase */}
                    {phase === 'final_jeopardy_question' && (
                        <div className="jeopardy-overview__final">
                            <h3>Final Jeopardy — Question</h3>
                            <p className="jeopardy-overview__final-category">
                                Category: <strong>{boardConfig.finalJeopardy.category}</strong>
                            </p>
                            <div className="jeopardy-overview__clue-question">{boardConfig.finalJeopardy.question}</div>
                            <div className="jeopardy-overview__timer">
                                <span className={`jeopardy-overview__timer-count${localTimer <= 5 ? ' --low' : ''}`}>{localTimer}s</span>
                            </div>
                            <div className="jeopardy-overview__clue-answer">
                                <strong>Host answer: </strong>{boardConfig.finalJeopardy.answer}
                            </div>
                            <button className="jeopardy-overview__reveal-btn" onClick={handleRevealFinalQuestion}>
                                → Reveal &amp; Score Answers
                            </button>
                        </div>
                    )}

                    {/* Final Jeopardy - Evaluation phase */}
                    {phase === 'final_jeopardy_evaluation' && (
                        <div className="jeopardy-overview__final">
                            <h3>Final Jeopardy — Scoring</h3>
                            <div className="jeopardy-overview__clue-question">{boardConfig.finalJeopardy.question}</div>
                            <div className="jeopardy-overview__clue-answer">
                                <strong>Answer: </strong>{boardConfig.finalJeopardy.answer}
                            </div>
                            <div className="jeopardy-overview__eval-teams">
                                {teams.map(team => {
                                    const evaluated = gameState.finalEvaluations[team.id];
                                    return (
                                        <div
                                            key={team.id}
                                            className="jeopardy-overview__eval-team"
                                            style={{ borderLeftColor: team.color }}
                                        >
                                            <span style={{ flex: 1 }}>{team.avatar} {team.name}</span>
                                            <span style={{ color: 'var(--j-muted)', fontSize: '0.85rem' }}>Wager: ${wagers[team.id] ?? 0}</span>
                                            {evaluated !== undefined && evaluated !== null ? (
                                                <span className={`jeopardy-overview__eval-result${evaluated ? ' --correct' : ' --wrong'}`}>
                                                    {evaluated ? '✓ Correct' : '✗ Wrong'}
                                                </span>
                                            ) : (
                                                <div className="jeopardy-overview__eval-buttons">
                                                    <button
                                                        className="jeopardy-overview__correct-btn"
                                                        onClick={() => handleFinalEvaluation(team.id, true)}
                                                    >
                                                        ✓ +${wagers[team.id] ?? 0}
                                                    </button>
                                                    <button
                                                        className="jeopardy-overview__incorrect-btn"
                                                        onClick={() => handleFinalEvaluation(team.id, false)}
                                                    >
                                                        ✗ −${wagers[team.id] ?? 0}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Tie-breaker */}
                    {phase === 'tie_breaker' && (
                        <div className="jeopardy-overview__final">
                            <h3>⚡ Sudden Death Tie-Breaker</h3>
                            <p style={{ color: 'var(--j-muted)', margin: 0 }}>
                                First team to buzz in and answer correctly wins!
                            </p>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                                {teams.map(team => (
                                    <div key={team.id} style={{ background: 'var(--j-card)', border: `2px solid ${team.color}`, borderRadius: 8, padding: '12px 20px', textAlign: 'center' }}>
                                        <div style={{ fontSize: '1.4rem' }}>{team.avatar}</div>
                                        <div style={{ fontWeight: 700 }}>{team.name}</div>
                                        <div style={{ color: 'var(--j-gold)', fontWeight: 800 }}>${team.score.toLocaleString()}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
                                {teams.map(team => (
                                    <button
                                        key={team.id}
                                        className="jeopardy-overview__advance-btn"
                                        style={{ background: team.color, color: '#000' }}
                                        onClick={() => {
                                            confirmAction({
                                                title: 'Declare Winner',
                                                message: `${team.avatar} ${team.name} wins the tie-breaker?`,
                                                onConfirm: () => pushState({ phase: 'game_over' }),
                                            });
                                        }}
                                    >
                                        {team.avatar} {team.name} Wins
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Game Over */}
                    {phase === 'game_over' && (
                        <div className="jeopardy-overview__game-over">
                            <h2>🏆 Game Over!</h2>
                            <div className="jeopardy-overview__podium">
                                {sortedTeams.slice(0, Math.min(3, sortedTeams.length)).map((team, idx) => (
                                    <div
                                        key={team.id}
                                        className={`jeopardy-overview__podium-place jeopardy-overview__podium-place--${idx + 1}`}
                                    >
                                        <span className="jeopardy-overview__podium-rank">
                                            {idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}
                                        </span>
                                        <span style={{ fontSize: '1.8rem' }}>{team.avatar}</span>
                                        <span style={{ fontWeight: 700 }}>{team.name}</span>
                                        <span className="jeopardy-overview__podium-score">${team.score.toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                            {sortedTeams.length > 3 && (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                                    {sortedTeams.slice(3).map((team, idx) => (
                                        <div key={team.id} style={{ color: 'var(--j-muted)', fontSize: '0.9rem' }}>
                                            {idx + 4}. {team.avatar} {team.name} — ${team.score.toLocaleString()}
                                        </div>
                                    ))}
                                </div>
                            )}
                            <button className="jeopardy-overview__reset-btn" onClick={handleResetGame}>
                                ↺ Play Again
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Custom confirm dialog */}
            {confirmOpts && (
                <ConfirmDialog
                    {...confirmOpts}
                    onCancel={() => setConfirmOpts(null)}
                    onConfirm={() => {
                        confirmOpts.onConfirm();
                        setConfirmOpts(null);
                    }}
                />
            )}
        </div>
    );
};
